import { Box, Typography, Card, CardContent, CardActionArea, Grid, Divider } from '@mui/material';
import Edit from '@mui/icons-material/EditOutlined';
import Folder from '@mui/icons-material/FolderOpenOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import React from 'react';

interface PlanCreationOptionsProps {
  onGenerateAI: () => void;
  onManual: () => void;
  onTemplate: () => void;
}

const CARD_HEIGHT = 260;

export default function PlanCreationOptions({
  onGenerateAI,
  onManual,
  onTemplate,
}: PlanCreationOptionsProps) {
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, background: '#FAFBFC', p: 0 }}>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 4 }}>
        Create a new plan
      </Typography>
      <Typography variant="h6" fontWeight={500} sx={{ mb: 2 }}>
        Start from scratch
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: CARD_HEIGHT }}>
            <CardActionArea onClick={onGenerateAI} sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <img src="/sparkles.svg" alt="AI" style={{ height: 32, width: 32 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Generate with AI
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                  Use AI tools to quickly draft a personalized treatment plan, saving time while
                  ensuring accuracy.
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  color="#6C63FF"
                  fontWeight={500}
                  sx={{ mt: 'auto', pt: 2, pb: 2 }}
                >
                  Continue <ChevronRight sx={{ fontSize: 18, ml: 0.5 }} />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: CARD_HEIGHT }}>
            <CardActionArea onClick={onManual} sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <Edit sx={{ color: '#6C63FF', fontSize: 32 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Create plan Manually
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                  Build your plan from scratch - using bespoke tasks, or from your library. This
                  method is best used when you already know what the patient needs or you would
                  rather have full control over the plan notes.
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  color="#6C63FF"
                  fontWeight={500}
                  sx={{ mt: 'auto', pt: 2, pb: 2 }}
                >
                  Get started <ChevronRight sx={{ fontSize: 18, ml: 0.5 }} />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
      <Typography variant="h6" fontWeight={500} sx={{ mb: 2 }}>
        Start from a Template
      </Typography>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardActionArea onClick={onTemplate}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Folder sx={{ color: '#6C63FF', fontSize: 32 }} />
              <Typography variant="h6" fontWeight={600}>
                Use saved tasks & templates
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select from your saved treatment plans, or combine saved tasks to build a custom plan.
            </Typography>
            <Box display="flex" alignItems="center" color="#6C63FF" fontWeight={500}>
              Browse templates <ChevronRight sx={{ fontSize: 18, ml: 0.5 }} />
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Box>
  );
}
